import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { AvailabilityEffect } from '../types';
import {
  browserTimezone,
  buildStartTimeIso,
  buildWeeklyRRule,
  durationFromTimes,
  type ScheduleDraft,
  WEEKDAYS,
} from '../utils/schedule';

/** The schedule fields a saved window carries; the container composes these
 * with effect + scope (provider_id) + encounter_type_id from the current
 * picker context. */
export interface AvailabilityScheduleInput {
  rrule: string | null;
  start_time: string;
  duration_minutes: number;
  timezone: string;
}

const DEFAULT_DRAFT: ScheduleDraft = {
  days: ['MO', 'TU', 'WE', 'TH', 'FR'],
  startTime: '09:00',
  endTime: '17:00',
};

export interface AvailabilityRuleDialogProps {
  open: boolean;
  mode: 'add' | 'edit';
  effect: AvailabilityEffect;
  /** Inherited context, shown read-only — set via the pickers, not here. */
  scopeLabel: string;
  encounterTypeName: string;
  /** Seed values for edit mode; add mode uses sensible defaults. */
  initial?: ScheduleDraft;
  isSaving?: boolean;
  onClose: () => void;
  onSubmit: (schedule: AvailabilityScheduleInput) => void;
}

/** Add or edit one recurring weekly availability window. The window's owner
 * (clinic default vs a clinician) and appointment type are inherited from the
 * manager's pickers and shown read-only, so this form only asks "when". */
export function AvailabilityRuleDialog({
  open,
  mode,
  effect,
  scopeLabel,
  encounterTypeName,
  initial,
  isSaving = false,
  onClose,
  onSubmit,
}: AvailabilityRuleDialogProps) {
  const [days, setDays] = useState<string[]>(DEFAULT_DRAFT.days);
  const [startTime, setStartTime] = useState(DEFAULT_DRAFT.startTime);
  const [endTime, setEndTime] = useState(DEFAULT_DRAFT.endTime);

  // Re-seed each time the dialog opens (add → defaults, edit → the rule).
  useEffect(() => {
    if (!open) return;
    const seed = initial ?? DEFAULT_DRAFT;
    setDays(seed.days);
    setStartTime(seed.startTime);
    setEndTime(seed.endTime);
  }, [open, initial]);

  const duration = durationFromTimes(startTime, endTime);
  const valid = days.length > 0 && duration > 0;
  const helperText = useMemo(() => {
    if (days.length === 0) return 'Pick at least one day.';
    if (duration <= 0) return 'End time must be after start time.';
    return `Times are in ${browserTimezone()}.`;
  }, [days.length, duration]);

  const noun = effect === 'open' ? 'open hours' : 'block';
  const title = `${mode === 'add' ? 'Add' : 'Edit'} ${noun}`;

  const handleSubmit = () => {
    if (!valid) return;
    onSubmit({
      rrule: buildWeeklyRRule(days),
      start_time: buildStartTimeIso(startTime),
      duration_minutes: duration,
      timezone: browserTimezone(),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>{title}</DialogTitle>
      <DialogContent>
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: 'wrap', mt: 0.5, mb: 2 }}
          useFlexGap
        >
          <Chip size="small" variant="outlined" label={scopeLabel} />
          <Chip size="small" variant="outlined" label={encounterTypeName} />
        </Stack>

        <Stack
          component="form"
          spacing={2}
          onSubmit={event => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <ToggleButtonGroup
            size="small"
            value={days}
            onChange={(_event, value: string[]) => setDays(value)}
            aria-label="Days of week"
            sx={{ flexWrap: 'wrap' }}
          >
            {WEEKDAYS.map(day => (
              <ToggleButton
                key={day.code}
                value={day.code}
                aria-label={day.label}
              >
                {day.short}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Stack direction="row" spacing={1.5}>
            <TextField
              type="time"
              size="small"
              label="Start"
              value={startTime}
              onChange={event => setStartTime(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
            <TextField
              type="time"
              size="small"
              label="End"
              value={endTime}
              onChange={event => setEndTime(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              error={duration <= 0}
              fullWidth
            />
          </Stack>

          <Typography
            variant="caption"
            color={valid ? 'text.secondary' : 'error'}
            sx={{ minHeight: 18 }}
          >
            {helperText}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!valid || isSaving}
          sx={{ textTransform: 'none' }}
        >
          {mode === 'add' ? 'Add' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
